import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Application} from "../Application";
import {Animatable, Ease} from "./Animatable";
import {BitmapText, BitmapTextStyle} from "./BitmapText";
import {IPoint} from "./Geom";
import {Callback} from "./Reflection";
import {Signal} from "./Signal";
import {Sprite} from "./Sprite";

export class TextInput extends BitmapText {

    public get isEditing() {
        return this._editing;
    }

    get cursorDisplay(): PIXI.DisplayObject | undefined {
        return this._cursorDisplay;
    }

    set cursorDisplay(value: PIXI.DisplayObject | undefined) {
        this._cursorDisplay = value;
        if (value) {
            if (!value.parent) {
                this.parent.addChildAt(value, this.parent.getChildIndex(this) + 1);
                value.visible = false;
            }
        } else {
            this.stopCursor();
        }
    }

    get pattern(): RegExp | undefined {
        return this._pattern;
    }

    set pattern(value: RegExp | undefined) {
        this._pattern = value;
    }

    public readonly interactionField = new Sprite(PIXI.Texture.EMPTY);

    public readonly onStartEdit = Signal<(input: TextInput) => void>();
    public readonly onStopEdit = Signal<(input: TextInput) => void>();

    public readonly keyboardControlRect = {x: 0, y: 0, width: 0, height: 0};
    // isActiveKeyboardControl(): boolean {
    //     return !this._editing && this.worldVisible && this.parent && this.parent.interactiveChildren;// && !Component.get(CScreenLockManager).isLocked;
    // }
    // getKeyboardControlRect(display: PIXI.DisplayObject): IRectangle {
    //     return this.keyboardControlRect;
    // }
    // activateKeyboardControl(type: KeyboardActionType): void {
    //     if (type == KeyboardActionType.Press && this.visible && !Component.get(CScreenLockManager).isLocked) {
    //         // console.log('activateKeyboardControl');
    //         if(!this._editing) {
    //             this.startEdit();
    //         }
    //     }
    // }
    // onKeyboardFocusIn?(): void {
    //     // console.log('onKeyboardFocusIn');
    //     this.startEdit();
    // }
    // onKeyboardFocusOut?(): void {
    //     // console.log('onKeyboardFocusOut');
    //     this.stopEdit();
    // }

    private readonly input = document.createElement("input");
    private _editing = false;

    private _cursorDisplay?: PIXI.DisplayObject;
    private readonly _cursorTweenObj: { value: number, children?: any[] } = {value: 1};
    private readonly _cursorTweenParams = {
        duration: 0.1,
        value: 0,
        repeat: -1,
        yoyo: true,
        delay: 0.5,
        repeatDelay: 0.5,
    };
    private _lastText: string = "";
    private _pattern?: RegExp;
    private readonly _onTouchStart: Callback;

    constructor(text: string, style: BitmapTextStyle, anchor?: IPoint) {
        super(text, style, anchor);

        this.input.style.position = "fixed";
        this.input.style.top = "0";
        this.input.style.left = "0";
        this.input.style.opacity = "0";
        this.input.style.textIndent = "-9999em"; // ie11
        this.input.style.caretColor = "transparent"; // safari
        this.input.style.zIndex = "-1";
        this.input.style.width = "1px";
        this.input.style.height = "1px";
        this.input.style.fontSize = "20px";
        document.body.appendChild(this.input);

        this.interactionField.on("pointertap", () => {
            // console.log('pointertap');
            this.startEdit();
        });
        this.input.onfocus = () => {
            // console.log('onfocus');
            this.startEdit();
        };
        this.input.onblur = () => {
            // console.log('onblur');
            this.stopEdit();
        };
        this._onTouchStart = () => {
            this.stopEdit();
        };
        window.addEventListener("touchstart", this._onTouchStart, false);
        this.input.oninput = () => {
            if (this._pattern && !this._pattern.test(this.input.value)) {
                this.input.value = this._lastText;
            } else {
                this.text = this.input.value;
                if (this.text.length > 0 && (this.textWidth > this.textAreaWidth || this.textHeight > this.textAreaHeight)) {
                    this.text = this._lastText;
                    this.input.value = this._lastText;
                } else {
                    this._lastText = this.input.value;
                }
                this.updateCursorPosition();
            }
        };
        this.input.onkeydown = (e) => {
            switch (parseInt(e.key, 10) || e.keyCode) {
                case HLF.Input.KeyCodes.ENTER:
                case HLF.Input.KeyCodes.ESC:
                case HLF.Input.KeyCodes.TAB:
                    e.preventDefault();
                    e.stopPropagation();
                    this.stopEdit();
                    return false;
                case HLF.Input.KeyCodes.UP:
                case HLF.Input.KeyCodes.DOWN:
                case HLF.Input.KeyCodes.LEFT:
                case HLF.Input.KeyCodes.RIGHT:
                case HLF.Input.KeyCodes.PAGE_UP:
                case HLF.Input.KeyCodes.PAGE_DOWN:
                case HLF.Input.KeyCodes.HOME:
                case HLF.Input.KeyCodes.END:
                    return false;
            }
            return true;
        };
    }

    public onLayout() {
        super.onLayout();
        const anchor = this.anchor as IPoint;
        this.interactionField.x = this.x - anchor.x * this.textAreaWidth;
        this.interactionField.y = this.y - anchor.y * this.textAreaHeight;
        this.interactionField.width = this.textAreaWidth;
        this.interactionField.height = this.textAreaHeight;
        this.interactionField.interactive = true;
        this.interactionField.buttonMode = true;
        this.parent.addChildAt(this.interactionField, this.parent.getChildIndex(this) + 1);
    }

    public startEdit() {
        if (!this._editing) {
            // console.log('startEdit');
            this._editing = true;
            document.body.appendChild(this.input);
            if (PIXI.utils.isMobile.any) {
                let p0 = new PIXI.Point(
                    -(this.anchor as IPoint).x * this.textAreaWidth,
                    -(this.anchor as IPoint).y * this.textAreaHeight);
                let p1 = new PIXI.Point(
                    p0.x + this.textAreaWidth,
                    p0.y + this.textAreaHeight);
                p0 = this.toGlobal(p0);
                p1 = this.toGlobal(p1);
                document.body.style.position = "fixed";
                document.body.style.left = "0";
                document.body.style.top = (-p0.y).toFixed(0) + "px";
                this.input.style.left = (p0.x + Application.instance.renderer.view.getBoundingClientRect().left).toFixed(0) + "px";
                this.input.style.width = (p1.x - p0.x).toFixed(0) + "px";
                this.input.style.height = (p1.y - p0.y).toFixed(0) + "px";
                Application.instance.resizeDisabled = true;
            }
            this.input.click();
            this.input.focus();
            this.input.value = this.text;
            this._lastText = this.text;
            this.startCursor();
            this.onStartEdit(this);
        }
    }

    public stopEdit() {
        if (this._editing) {
            // console.log('stopEdit');
            try {
                this.input.remove();
            } catch (e) {
                //
            }
            if (PIXI.utils.isMobile.any) {
                document.body.style.position = "relative";
                document.body.style.left = "0";
                document.body.style.top = "0";
                this.input.style.left = "0";
                this.input.style.width = "1px";
                this.input.style.height = "1px";
                Application.instance.resizeDisabled = false;
            }
            this.stopCursor();
            this.text = this.input.value;
            this._editing = false;
            this.onStopEdit(this);
        }
    }

    public destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.onStartEdit.remove();
        this.onStopEdit.remove();
        this.stopEdit();
        if (this.interactionField.parent) {
            this.interactionField.destroy();
        }
        this._cursorDisplay = undefined;
        if (this.input.parentNode) {
            this.input.parentNode.removeChild(this.input);
        }
        if (this._onTouchStart) {
            window.removeEventListener("touchstart", this._onTouchStart, false);
        }
        super.destroy(options);
    }

    private update(time: number) {
        if (this._cursorDisplay) {
            this._cursorDisplay.alpha = this._cursorTweenObj.value;
        }
    }

    private updateCursorPosition() {
        if (this._cursorDisplay) {
            this.validate();
            const lastChild = this.children.length > 0 ? this.children[this.children.length - 1] as PIXI.Sprite : null;
            const lastChildX = lastChild ? lastChild.x : 0;
            const lastChildWidth = lastChild ? lastChild.width : 0;
            this._cursorDisplay.x = this.x + (lastChildX + lastChildWidth) * this.scale.x;
            this._cursorDisplay.y = this.y + (this.anchor as IPoint).y * (this.height || this.fontSize) * this.scale.y;
        }
    }

    private startCursor() {
        if (this._editing && this._cursorDisplay) {
            this.stopCursor();
            this._cursorDisplay.visible = true;
            this._cursorTweenObj.value = 1;
            this.updateCursorPosition();
            PIXI.ticker.shared.add(this.update, this);
            Animatable.tweenTo.call(this._cursorTweenObj, this._cursorTweenParams.duration, Ease.none, this._cursorTweenParams);
        }
    }

    private stopCursor() {
        PIXI.ticker.shared.remove(this.update, this);
        Animatable.killTweens.call(this._cursorTweenObj);
        if (this._cursorDisplay) {
            this._cursorDisplay.visible = false;
        }
    }
}
